/**
 * Shared payment flow used by both the portrait CheckoutScreen and the
 * landscape CartScreen (which has inline payment selection).
 *
 * Reads/writes state via useKioskStore.getState() to keep the function
 * free of React hooks — callers just invoke it.
 */

import { gql } from '@/lib/api'
import { ACCEPT_TERMS, CREATE_ORDER_INTENT, CREATE_PAYMENT_INTENT, FINALIZE_ORDER, FINALIZE_PAYMENT, RECEIPT_QUERY, TERMS_QUERY } from '@/lib/queries'
import type { PaymentMethod } from '@/lib/types'
import { uuid } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

interface FinalizeOrderResult {
  finalizeOrderIntent?: {
    id: string
    receiptViewKey: string
    receiptPrinter?: { id: string; name: string } | null
  }
}

export async function processPayment(locale: string): Promise<void> {
  const store = useKioskStore.getState()
  const {
    config,
    parseToken,
    cart,
    menuType,
    tipPercent,
    orderMessage,
    payMethod,
    paymentMethods,
    selectedDeliveryLoc,
    selectedTimeSlot,
    timeSlots,
    posTerminalId,
    posTerminalProvider,
    receiptPrinterId,
    setError,
    setProcessing,
    setStep,
    setSwishQr,
  } = store

  if (!config || !parseToken) {
    setError(locale === 'en' ? 'You must log in first. Press and hold the logo for 5 seconds to log in.' : 'Du måste logga in först. Håll in loggan i 5 sekunder för att logga in.')
    return
  }

  // Pick the payment method that matches the connected terminal's provider.
  // posTerminalProvider was set to the selected terminal's provider in KioskShell
  // (Stripe terminal → 'Stripe'; Nets terminal → 'NetsConnectCloud'). This makes
  // sure we don't pick Stripe method when only a Nets terminal is online (or vice versa).
  // For Swish-via-Stripe we can still fall back to any Stripe method even without
  // a card terminal, since Swish doesn't need one.
  const stripeMethod = paymentMethods.find((m) => m.paymentProvider === 'Stripe')
  const netsMethod = paymentMethods.find((m) => m.paymentProvider === 'NetsConnectCloud')
  const standaloneSwishMethod = paymentMethods.find((m) => m.paymentProvider === 'Swish')

  // LocoPOS-style method selection: match the online terminal's provider to the
  // payment method of the same provider. Same logic as LocoPOS order-main uses
  // in CheckoutDialog.tsx — method provider MUST match terminal provider.
  const methodProviders = paymentMethods.map((m) => m.paymentProvider).join(',')
  const diagnostic = `[v83b · term=${posTerminalProvider ?? 'none'} · methods=${methodProviders || 'none'}]`

  let cardMethod: PaymentMethod | null = null
  if (payMethod === 'swish') {
    // Prefer standalone Swish (provider="Swish") om tillgängligt, annars Stripe-integrerad Swish
    cardMethod = standaloneSwishMethod ?? stripeMethod ?? null
    if (!cardMethod) {
      setError(`Swish saknas ${diagnostic}`)
      return
    }
  } else {
    if (!posTerminalId || !posTerminalProvider) {
      setError(`Ingen online-betalterminal hittades på kassan i LocoPOS. ${diagnostic}`)
      return
    }
    if (posTerminalProvider === 'Stripe') cardMethod = stripeMethod ?? null
    else if (posTerminalProvider === 'NetsConnectCloud') cardMethod = netsMethod ?? null
    if (!cardMethod) {
      setError(`Terminal är ${posTerminalProvider} men ingen matchande betalmetod på kassan. ${diagnostic}`)
      return
    }
  }
  const useStripeFlow = cardMethod.paymentProvider === 'Stripe'
  const useStandaloneSwish = cardMethod.paymentProvider === 'Swish'
  console.log('[Kiosk v83b] Selected payment method:', {
    provider: cardMethod.paymentProvider,
    id: cardMethod.id,
    posTerminalId,
    posTerminalProvider,
    useStripeFlow,
    useStandaloneSwish,
  })

  // Addon-rabatt: f\u00f6r varje addon-item med menuPrice > price, diff * count.
  // Detta \u00e4r v\u00e4rdet som LocoPOS ska dra av fr\u00e5n items-summan f\u00f6r att matcha
  // v\u00e5r cart-total. Beh\u00f6vs bara for ADDON-rader (isAddon=true); V.2-alternatives
  // refererar MenuAddonProducts direkt s\u00e5 servern r\u00e4knar r\u00e4tt pris native.
  const addonDiscount = cart.reduce((s, i) => {
    if (!i.isAddon || i.menuPrice == null || i.menuPrice <= i.price) return s
    return s + (i.menuPrice - i.price) * i.count
  }, 0)
  const totalDiscount = addonDiscount
  const totalAmount = cart.reduce((s, i) => s + i.price * i.count, 0) // vad kunden ser
  const tipAmount = Math.round((totalAmount * tipPercent) / 100)
  const grandTotal = totalAmount + tipAmount

  setProcessing(true)
  setError(null)

  try {
    const orderItems = cart.map((item) => ({
      count: item.count,
      // OBS: productPrice skickas INTE p\u00e5 varken huvud-OrderItem.product eller
      // OrderAlternative.addonProduct \u2014 LocoPOS returnerar "Varorna har uppdaterats"
      // \u00e4ven n\u00e4r v\u00e4rdet matchar servern. For V.2-override anv\u00e4nds discountAmount
      // p\u00e5 OrderIntentInput i st\u00e4llet (se v2Discount-ber\u00e4kning nedan).
      product: { productId: item.productId, productStateId: item.stateId },
      alternatives: item.alternatives.map((a) => ({
        alternativeGroupId: a.alternativeGroupId,
        alternativeItemName: a.alternativeItemName,
        count: a.count,
        ...(a.addonProduct
          ? {
              addonProduct: {
                productId: a.addonProduct.productId,
                productStateId: a.addonProduct.productStateId,
              },
            }
          : {}),
      })),
    }))

    // Aggregate per-item messages + the order-level note into a single message string.
    // LocoPOS OrderIntentInput.message is the single reliable place — kitchen staff will
    // see all requests there.
    const itemNotes = cart.filter((i) => i.message?.trim()).map((i) => `${i.name}: ${i.message?.trim()}`)
    const combinedMessage = [...itemNotes, orderMessage?.trim()].filter(Boolean).join('\n')

    const selectedSlotValue = timeSlots.find((s) => s.id === selectedTimeSlot)?.value

    // 1. Create order intent (with auto-accept terms on first failure)
    const orderInput = {
      rid: config.restaurantId,
      input: {
        idempotencyKey: uuid(),
        appLabel: 'order',
        type: menuType || 'TakeAway',
        transactionChannel: 'SelfCheckout',
        installationId: `kiosk-${config.tillId}`,
        items: orderItems,
        // Servern räknar items + tip som "actual total". Om vi bara skickar
        // cart-summan (utan dricks) failar valideringen med
        // "Expected total amount is not equal to actual value" så fort
        // tipAmount > 0. Därfor: grandTotal = totalAmount + tipAmount.
        expectedTotalAmount: grandTotal,
        // discountAmount: Addon- + V.2-alt-kampanjpris vs menypris. Servern
        // r\u00e4knar items vid menyns pris; vi drar av kampanj-differensen s\u00e5
        // slutsumman matchar expectedTotalAmount (vad kund ser).
        ...(totalDiscount > 0 ? { discountAmount: totalDiscount } : {}),
        tip: tipAmount,
        message: combinedMessage || undefined,
        ...(selectedSlotValue ? { deliveryTime: selectedSlotValue } : {}),
        ...(selectedDeliveryLoc ? { deliveryLocationId: selectedDeliveryLoc } : {}),
        ...(config.tillId && config.tillId !== 'test' ? { tillId: config.tillId } : {}),
        ...(receiptPrinterId ? { receiptPrinterId } : {}),
        ...(payMethod !== 'swish' && posTerminalId ? { posTerminalId } : {}),
      },
    }

    console.log('[Kiosk] createOrderIntent input:', {
      tillId: config.tillId,
      receiptPrinterId,
      posTerminalId,
      payMethod,
    })

    let orderData: Awaited<ReturnType<typeof gql>>
    try {
      orderData = await gql(config.apiEndpoint, CREATE_ORDER_INTENT, orderInput, parseToken)
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code || ''
      const msg = e instanceof Error ? e.message : ''
      if (code === 'USER_NEEDS_TO_ACCEPT_TERMS_OF_USE' || msg.includes('acceptera')) {
        const termsData = await gql(config.apiEndpoint, TERMS_QUERY)
        const termsId = termsData.currentTermsOfService?.id
        if (termsId) await gql(config.apiEndpoint, ACCEPT_TERMS, { id: termsId }, parseToken)
        orderInput.input.idempotencyKey = uuid()
        orderData = await gql(config.apiEndpoint, CREATE_ORDER_INTENT, orderInput, parseToken)
      } else {
        throw e
      }
    }

    const order = orderData.createOrderIntent

    // 2. Create payment intent — Stripe flow kept identical to preserve existing Kungshallen/MoS behavior.
    // Nets (NetsConnectCloud) flow is separate and doesn't send stripePaymentMethod/paymentProvider.
    const paymentInputBase = {
      idempotencyKey: uuid(),
      totalAmount: order.totalAmountPayable,
      vatAmount: order.vatAmountPayable,
      tipAmount,
      paymentMethodId: cardMethod.id,
      ...(config.tillId && config.tillId !== 'test' ? { tillId: config.tillId } : {}),
    }
    const paymentInput = useStandaloneSwish
      ? {
          // Standalone Swish (ej via Stripe) — paymentProvider = 'Swish',
          // ingen terminal, ingen stripePaymentMethod.
          ...paymentInputBase,
          paymentProvider: 'Swish',
        }
      : useStripeFlow
        ? {
            ...paymentInputBase,
            paymentProvider: 'Stripe',
            stripePaymentMethod: payMethod === 'swish' ? 'Swish' : 'CardPresent',
            ...(payMethod !== 'swish' && posTerminalId ? { posTerminalId } : {}),
          }
        : {
            // NetsConnectCloud — needs terminal, no stripePaymentMethod/paymentProvider
            ...paymentInputBase,
            ...(posTerminalId ? { posTerminalId } : {}),
          }

    const payData = await gql(
      config.apiEndpoint,
      CREATE_PAYMENT_INTENT,
      {
        oid: order.id,
        input: paymentInput,
      },
      parseToken,
    )

    // Persist debug info so admin modal can show the last payment-intent response
    try {
      const pi = payData?.createPaymentIntent
      useKioskStore
        .getState()
        .setLastPaymentDebug(`state=${pi?.state} actionType=${pi?.action?.type ?? 'none'} statusUrl=${pi?.action?.statusUrl ? 'yes' : 'no'} qrCode=${pi?.action?.qrCodeValue ? 'yes' : 'no'}`)
      console.log('[Kiosk v76] PaymentIntent response:', pi)
    } catch {
      /* debug logging only — ignore */
    }

    const payment = payData.createPaymentIntent
    const action = payment.action

    // 3. Handle payment action
    const abortRef = { current: false }
    ;(window as unknown as Record<string, unknown>).__kioskAbort = abortRef
    ;(window as unknown as Record<string, unknown>).__kioskCancelInfo = {
      paymentIntentId: payment.id,
      orderIntentId: order.id,
      restaurantId: config.restaurantId,
    }

    if (action?.type === 'OpenSwish' && action?.qrCodeValue) {
      setSwishQr(action.qrCodeValue)
      setStep('paying')
      await pollSwishAndFinalize(config.apiEndpoint, parseToken, abortRef, payment.id, order.id, payment.finalizeAccessToken, order.finalizeAccessToken, action.deadline, grandTotal)
    } else if (action?.statusUrl) {
      setStep('paying')
      await pollStatusAndFinalize(config.apiEndpoint, parseToken, abortRef, action.statusUrl, payment.id, order.id, payment.finalizeAccessToken, order.finalizeAccessToken, grandTotal)
    } else {
      throw new Error('Kunde inte starta betalning. Kontrollera att kassan har en betalterminal kopplad.')
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Något gick fel'
    if (msg !== 'ABORTED') setError(msg)
    if (useKioskStore.getState().step === 'paying') {
      // On error, return to cart (payment is always initiated from cart in both modes)
      setStep('cart')
    }
  } finally {
    setProcessing(false)
    setSwishQr(null)
    delete (window as unknown as Record<string, unknown>).__kioskAbort
    delete (window as unknown as Record<string, unknown>).__kioskCancelInfo
  }
}

// Swish: try finalize every 3s (same pattern as LocoOrder.tsx)
async function pollSwishAndFinalize(
  apiEndpoint: string,
  token: string,
  abortRef: { current: boolean },
  paymentIntentId: string,
  orderIntentId: string,
  paymentFinalizeToken: string | null,
  _orderFinalizeToken: string | null,
  deadlineStr: string | null,
  grandTotal: number,
) {
  const deadline = deadlineStr ? new Date(deadlineStr).getTime() : Date.now() + 180_000

  while (Date.now() < deadline && !abortRef.current) {
    await new Promise((r) => setTimeout(r, 3000))
    if (abortRef.current) throw new Error('ABORTED')

    try {
      // Swish poll: success if FINALIZE_PAYMENT resolves without throwing
      await gql(
        apiEndpoint,
        FINALIZE_PAYMENT,
        {
          pid: paymentIntentId,
          t: paymentFinalizeToken || token,
        },
        token,
      )
    } catch (fErr) {
      const msg = fErr instanceof Error ? fErr.message : ''
      if (/cancel|fail|decline|expired|avbruten|reject/i.test(msg)) {
        throw new Error('Swish-betalning avbruten')
      }
      // Not ready yet — keep polling
      continue
    }

    // Payment confirmed — the order MUST finalize successfully before we show receipt
    let finalData: FinalizeOrderResult | undefined
    try {
      finalData = await gql(apiEndpoint, FINALIZE_ORDER, { oid: orderIntentId }, token)
    } catch (orderErr) {
      const em = orderErr instanceof Error ? orderErr.message : 'okänt fel'
      throw new Error(`Kunde inte slutföra ordern efter Swish: ${em}`)
    }

    if (!finalData?.finalizeOrderIntent) {
      throw new Error('Ordern slutfördes inte efter Swish-betalningen')
    }

    await showReceipt(apiEndpoint, orderIntentId, finalData, grandTotal)
    return
  }
  if (abortRef.current) throw new Error('ABORTED')
  throw new Error('Swish-betalningen tog för lång tid. Kontrollera Swish-appen.')
}

// Terminal: poll statusUrl every 2s (same pattern as UseIntegratedTerminal.tsx)
async function pollStatusAndFinalize(
  apiEndpoint: string,
  token: string,
  abortRef: { current: boolean },
  statusUrl: string,
  paymentIntentId: string,
  orderIntentId: string,
  paymentFinalizeToken: string | null,
  orderFinalizeToken: string | null,
  grandTotal: number,
) {
  const deadline = Date.now() + 300_000

  while (Date.now() < deadline && !abortRef.current) {
    await new Promise((r) => setTimeout(r, 2000))
    if (abortRef.current) throw new Error('ABORTED')

    let status = ''
    try {
      const resp = await fetch(statusUrl).then((r) => r.json())
      status = String(resp?.status ?? '').toUpperCase()
    } catch {
      continue // network blip — keep polling
    }

    // Still pending — keep polling
    if (!status || status === 'CREATED' || status === 'PENDING' || status === 'PROCESSING' || status.includes('REQUIRES')) continue

    // Terminal reported a failure — bail out with a clear error
    if (/FAIL|CANCEL|DECLIN|EXPIR|ABORT|REJECT/.test(status)) {
      throw new Error(`Betalning misslyckades (${status})`)
    }

    // Success-ish status (DONE, CONFIRMED, SUCCEEDED, READY, EXECUTED, …)
    // Finalize the payment — if it throws with a failure-like message, treat as failure
    try {
      await gql(
        apiEndpoint,
        FINALIZE_PAYMENT,
        {
          pid: paymentIntentId,
          t: paymentFinalizeToken || token,
        },
        token,
      )
    } catch (payErr) {
      const em = payErr instanceof Error ? payErr.message : ''
      if (/fail|cancel|declin|expir|reject|avbr/i.test(em)) {
        throw new Error(`Betalning avbröts: ${em}`)
      }
      // else: might already be finalized server-side — try to finalize order anyway
    }

    // Finalize the order — this MUST succeed for a real receipt
    let finalData: FinalizeOrderResult | undefined
    try {
      finalData = await gql(
        apiEndpoint,
        FINALIZE_ORDER,
        {
          oid: orderIntentId,
          t: orderFinalizeToken || token,
        },
        token,
      )
    } catch (orderErr) {
      const em = orderErr instanceof Error ? orderErr.message : 'okänt fel'
      throw new Error(`Kunde inte slutföra ordern: ${em}`)
    }

    if (!finalData?.finalizeOrderIntent) {
      throw new Error('Ordern slutfördes inte — betalningen gick sannolikt inte igenom')
    }

    await showReceipt(apiEndpoint, orderIntentId, finalData, grandTotal)
    return
  }

  if (abortRef.current) throw new Error('ABORTED')
  throw new Error('Betalningen tog för lång tid. Försök igen.')
}

async function showReceipt(apiEndpoint: string, orderIntentId: string, finalData: FinalizeOrderResult | undefined, grandTotal: number) {
  console.log('[Kiosk] Finalize result — receiptPrinter:', finalData?.finalizeOrderIntent?.receiptPrinter)
  if (finalData?.finalizeOrderIntent) {
    try {
      const rd = await gql(apiEndpoint, RECEIPT_QUERY, {
        oid: finalData.finalizeOrderIntent.id,
        vk: finalData.finalizeOrderIntent.receiptViewKey,
      })
      useKioskStore.getState().setReceiptHtml(rd.receipt.html)
    } catch {
      /* ok */
    }
  }

  const store = useKioskStore.getState()
  store.setCompletedOrder({
    items: [...store.cart],
    total: grandTotal,
    type: store.menuType,
    orderId: finalData?.finalizeOrderIntent?.id || orderIntentId,
    viewKey: finalData?.finalizeOrderIntent?.receiptViewKey || null,
  })
  store.clearCart()
  store.setStep('receipt')
}
